import type { AsyncJobSummary } from '@core/types/api/AsyncJobSummary/AsyncJobSummary';
import Enums from '@core/types/api/Enums';

import type { AsyncJobStatusConfig } from './AsyncJobStatusConfig';

export type AsyncJobStatusPresentation = {
  state: 'starting' | 'processing' | 'succeeded' | 'partial' | 'failed';
  tone: 'info' | 'warning' | 'success' | 'error';
  isInProgress: boolean;
  hasFailedItems: boolean;
  failedCount: number;
  progressPercent?: number;
  compactLabel: string;
  primaryLabel: string;
  secondaryLabel?: string;
};

export const getAsyncJobStatusPresentation = (
  job: AsyncJobSummary,
  config: AsyncJobStatusConfig,
): AsyncJobStatusPresentation => {
  const failedCount = job.itemsFailed;
  const hasFailedItems = failedCount > 0;
  const attemptedCount =
    job.itemsTotal > 0
      ? Math.min(job.itemsProcessed + failedCount, job.itemsTotal)
      : job.itemsProcessed + failedCount;
  const progressPercent = job.itemsTotal > 0 ? (attemptedCount / job.itemsTotal) * 100 : 0;

  if (job.status === Enums.AsyncJobStatus.Pending) {
    const label = 'Starting...';

    return {
      state: 'starting',
      tone: hasFailedItems ? 'warning' : 'info',
      isInProgress: true,
      hasFailedItems,
      failedCount,
      progressPercent,
      compactLabel: hasFailedItems ? `${label} ${failedCount} failed` : label,
      primaryLabel: label,
      secondaryLabel: hasFailedItems
        ? `${job.itemsProcessed} processed, ${failedCount} failed so far`
        : undefined,
    };
  }

  if (job.status === Enums.AsyncJobStatus.Processing) {
    const compactLabel = config.compactProcessingLabel(job, attemptedCount);

    return {
      state: 'processing',
      tone: hasFailedItems ? 'warning' : 'info',
      isInProgress: true,
      hasFailedItems,
      failedCount,
      progressPercent,
      compactLabel: hasFailedItems ? `${compactLabel} ${failedCount} failed` : compactLabel,
      primaryLabel: config.processingLabel(job, attemptedCount),
      secondaryLabel: hasFailedItems
        ? `${job.itemsProcessed} processed, ${failedCount} failed so far`
        : undefined,
    };
  }

  if (job.status === Enums.AsyncJobStatus.Completed && !hasFailedItems) {
    return {
      state: 'succeeded',
      tone: 'success',
      isInProgress: false,
      hasFailedItems,
      failedCount,
      compactLabel: 'Completed',
      primaryLabel: config.successLabel(job),
    };
  }

  if (job.status === Enums.AsyncJobStatus.Completed) {
    return {
      state: 'partial',
      tone: 'warning',
      isInProgress: false,
      hasFailedItems,
      failedCount,
      compactLabel: `${failedCount} Failed`,
      primaryLabel: 'Completed with Issues',
      secondaryLabel: `${job.itemsTotal - failedCount} succeeded, ${failedCount} failed`,
    };
  }

  return {
    state: 'failed',
    tone: 'error',
    isInProgress: false,
    hasFailedItems,
    failedCount,
    compactLabel: 'Failed',
    primaryLabel: config.failedTitle,
    secondaryLabel: config.failedMessage,
  };
};
